import React from "react";
import { Scale, FileSpreadsheet } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CourtDistribution, DecisionTypeDistribution } from "@/redux/api/courtScraper.rtkq";

interface DistributionChartsProps {
  courts?: CourtDistribution[];
  decisionTypes?: DecisionTypeDistribution[];
  isLoading?: boolean;
}

export const DistributionCharts: React.FC<DistributionChartsProps> = ({
  courts = [],
  decisionTypes = [],
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="animate-pulse bg-white border-slate-200">
          <CardContent className="p-6 h-56"></CardContent>
        </Card>
        <Card className="animate-pulse bg-white border-slate-200">
          <CardContent className="p-6 h-56"></CardContent>
        </Card>
      </div>
    );
  }

  const maxCourtCount = Math.max(...courts.map((c) => c.document_count), 1);
  const maxDecisionCount = Math.max(...decisionTypes.map((d) => d.document_count), 1);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Top Courts */}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader className="pb-3 border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <Scale className="w-4 h-4 text-indigo-600" />
            בתי משפט מובילים במסד הנתונים
          </CardTitle>
          <span className="text-xs text-slate-500 font-normal">לפי כמות מסמכים</span>
        </CardHeader>
        <CardContent className="p-4 space-y-2.5">
          {courts.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">אין נתונים להצגה</div>
          ) : (
            courts.slice(0, 6).map((court, idx) => {
              const pct = ((court.document_count / maxCourtCount) * 100).toFixed(0);
              return (
                <div key={idx} className="space-y-1">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-medium text-slate-700 truncate max-w-[70%]">
                      {court.court_name}
                    </span>
                    <span className="text-slate-500 font-mono">
                      {court.document_count.toLocaleString()} מסמכים
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-500 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Decision Types */}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader className="pb-3 border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-indigo-600" />
            התפלגות לפי סוג החלטה
          </CardTitle>
          <span className="text-xs text-slate-500 font-normal">פסק דין / גזר דין / החלטה</span>
        </CardHeader>
        <CardContent className="p-4 space-y-2.5">
          {decisionTypes.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">אין נתונים להצגה</div>
          ) : (
            decisionTypes.slice(0, 6).map((dt, idx) => {
              const pct = ((dt.document_count / maxDecisionCount) * 100).toFixed(0);
              return (
                <div key={idx} className="space-y-1">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-medium text-slate-700 truncate max-w-[70%]">
                      {dt.decision_type_name}
                    </span>
                    <span className="text-slate-500 font-mono">
                      {dt.document_count.toLocaleString()} מסמכים
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-500 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
};
